import { FC, useEffect, useState } from 'react';
import { Box, FormControlLabel, Modal, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { CircularProgress } from '@mui/material';

import CustomSelect from '../../../../../common/CustomSelect';
import CustomButton from '../../../../../common/CustomButton';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import {
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from '../../../../../store/services/customizeOptionsApi';

import styles from './GeneralSettings.module.scss';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { useDeleteAccountMutation } from '../../../../../store/services/authApi';
import { unregisterNotificationDevice } from '../../../../../services/notificationDevice';
import { logout } from '../../../../../store/slices/authSlice';
import BlueIOSSwitch from './BlueIOSSwitch';
import BellIcon from '../../../../../assets/icons/Bell.png';
import DollarIcon from '../../../../../assets/icons/Dollar.png';
import CheckmarkIcon from '../../../../../assets/icons/CheckmarkCheck.png';
import { currencyOptions } from '../../../../../constants/currency';
import InformationIcon from '../../../../../assets/icons/Info, Infornation.png';

type FormState = {
  show_notifications: boolean;
  two_factor_enabled: boolean;
  show_trip_budgets: boolean;
  currency: string;
  unit_system: string;
};

const unitOptions: { label: string; value: string }[] = [
  { label: 'Miles/°F', value: 'Imperial' },
  { label: 'Km/°C', value: 'Metric' },
];

const GeneralSettings: FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [deleteAccount, { isLoading: isDeletingAccount }] = useDeleteAccountMutation();
  const { data, isFetching } = useGetUserSettingsQuery();
  const [updateSettings] = useUpdateUserSettingsMutation();
  const [form, setForm] = useState<FormState | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const user = useAppSelector(state => state.user);

  useEffect(() => {
    if (data) {
      setForm(data);
    }
  }, [data]);

  const handleDeleteAccount = async () => {
    try {
      await unregisterNotificationDevice();
      await deleteAccount().unwrap();
      localStorage.removeItem('createAccount');
      localStorage.removeItem('userEmail');
      dispatch(logout());
      navigate('/sign-in', { replace: true });
    } catch {}
  };

  const handleToggle =
    (key: 'show_notifications' | 'two_factor_enabled' | 'show_trip_budgets') =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (!form) return;
      const updated = { ...form, [key]: checked };
      setForm(updated);
      updateSettings({ [key]: checked });
    };

  const handleCurrencyChange = (e: SelectChangeEvent<string>) => {
    if (!form) return;
    const currency = e.target.value;
    setForm({ ...form, currency });
    updateSettings({ currency });
  };

  const handleUnitLabelChange = (e: SelectChangeEvent<string>) => {
    if (!form) return;
    const label = e.target.value;
    const mapped = unitOptions.find(u => u.label === label)?.value ?? '';
    setForm({ ...form, unit_system: mapped });
    if (mapped) updateSettings({ unit_system: mapped });
  };

  const unitLabelValue = form ? (unitOptions.find(u => u.value === form.unit_system)?.label ?? '') : '';

  if (!form || isFetching) {
    return (
      <Box className={styles.generalSettings}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={`${styles.generalSettings} ${user.is_google_user ? styles.onlyGenerals : ''}`}>
      <Box className={styles.headerRow}>
        <Typography
          variant={isMobile ? 'h8Bold' : 'h7Bold'}
          sx={{ color: '#222B3A' }}
        >
          General Settings
        </Typography>
      </Box>

      <Box className={styles.rows}>
        <Box className={styles.switchWrapper}>
          <img
            src={BellIcon}
            className={styles.icon}
            alt="BellIcon"
          />
          <FormControlLabel
            className={styles.switchRow}
            control={
              <BlueIOSSwitch
                checked={form.show_notifications}
                onChange={handleToggle('show_notifications')}
              />
            }
            label={<Typography sx={{ color: '#222B3A' }}>Show Notifications</Typography>}
          />
        </Box>
        {user.is_premium && (
          <Box className={styles.switchWrapper}>
            <img
              src={CheckmarkIcon}
              className={styles.icon}
              alt="CheckmarkIcon"
            />
            <FormControlLabel
              className={styles.switchRow}
              control={
                <BlueIOSSwitch
                  checked={form.two_factor_enabled}
                  onChange={handleToggle('two_factor_enabled')}
                />
              }
              label={<Typography sx={{ color: '#222B3A' }}>Two-Factor Authentication</Typography>}
            />
          </Box>
        )}
        <Box className={styles.switchWrapper}>
          <img
            src={DollarIcon}
            className={styles.icon}
            alt="DollarIcon"
          />
          <FormControlLabel
            className={styles.switchRow}
            control={
              <BlueIOSSwitch
                checked={form.show_trip_budgets}
                onChange={handleToggle('show_trip_budgets')}
              />
            }
            label={
              <Typography sx={{ color: '#222B3A', display: 'flex', alignItems: 'center', gap: 1 }}>
                Show Trip Budgets
                <img
                  src={InformationIcon}
                  className={styles.additionalInfoImg}
                  alt="info"
                />
              </Typography>
            }
          />
        </Box>
        <Box className={styles.selectRow}>
          <CustomSelect
            name="currency"
            inputLabel="Currency"
            value={currencyOptions.find(c => c.code === form.currency)?.label ?? ''}
            data={currencyOptions.map(c => c.label)}
            handleChange={e => {
              const selectedLabel = e.target.value;
              const selectedCurrency = currencyOptions.find(c => c.label === selectedLabel)?.code ?? '';
              handleCurrencyChange({ target: { value: selectedCurrency } } as any);
            }}
          />
        </Box>
        <Box className={styles.selectRow}>
          <CustomSelect
            name="unitsofMeasure"
            inputLabel="Units of Measure"
            value={unitLabelValue}
            data={unitOptions.map(u => u.label)}
            handleChange={handleUnitLabelChange}
          />
        </Box>
      </Box>
      <Box className={styles.deleteButtonWrapper}>
        <CustomButton
          variant="outlined"
          disabled={isDeletingAccount}
          onClick={() => setOpenModal(true)}
          customClass={styles.deleteButton}
        >
          Delete Account
        </CustomButton>
      </Box>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 343,
            width: '90%',
            maxWidth: 400,
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            overflowY: 'auto',
            boxShadow: 24,
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
            <CloseIcon
              sx={{ width: 30, height: 30 }}
              onClick={() => setOpenModal(false)}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography
              id="modal-title"
              variant="h5"
              sx={{ fontWeight: 'bold', textAlign: 'center' }}
              color="#222B3A"
            >
              Are you sure?
            </Typography>
            <Typography
              id="modal-description"
              variant="body1"
              sx={{ color: 'text.secondary', textAlign: 'center' }}
            >
              You want to delete your account
            </Typography>

            <Box sx={{ mt: 3, width: '100%', display: 'flex', justifyContent: 'center', gap: 2, '& button': {
                  height: '44px',
                },
 }}>
              <CustomButton
                variant="outlined"
                onClick={() => setOpenModal(false)}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleDeleteAccount}
                customClass={styles.deleteButtonModal}
              >
                Delete
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default GeneralSettings;
